import { zodResolver } from '@hookform/resolvers/zod';
import { Button, EmptyState, IconButton, Input, Pill, SectionHeader } from '@cyphex/ui';
import { Plus, SlidersHorizontal, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useNotificationRules } from '../hooks/useNotificationSettings';
import { useRuleActions } from '../hooks/useRuleActions';
import { ToggleRow } from './ToggleRow';
const ruleSchema = z.object({
  name: z.string().trim().min(2, 'Give the rule a name.').max(60),
  keywords: z.string().trim().min(1, 'Add at least one keyword.'),
  urgent: z.boolean(),
});

type RuleFormValues = z.infer<typeof ruleSchema>;

function toKeywords(raw: string): string[] {
  return raw
    .split(',')
    .map((word) => word.trim())
    .filter(Boolean)
    .slice(0, 20);
}

/** Keyword alerts — the client half of `cyphex.notification_rules`. */
export function NotificationRulesEditor() {
  const rules = useNotificationRules();
  const { save, remove, toggle } = useRuleActions();

  const { register, handleSubmit, reset, formState } = useForm<RuleFormValues>({
    resolver: zodResolver(ruleSchema),
    defaultValues: { name: '', keywords: '', urgent: false },
  });

  const onSubmit = handleSubmit((values) => {
    save({ name: values.name, keywords: toKeywords(values.keywords), urgent: values.urgent });
    reset({ name: '', keywords: '', urgent: false });
  });

  return (
    <section aria-label="Custom notification rules">
      <SectionHeader title="Keyword alerts" variant="card" />

      {rules.length === 0 ? (
        <EmptyState
          icon={SlidersHorizontal}
          size="sm"
          tone="neutral"
          title="No custom rules yet"
          description="Get notified whenever a word you care about is mentioned anywhere."
        />
      ) : (
        <ul className="flex flex-col gap-1">
          {rules.map((rule) => (
            <li key={rule.id} className="flex items-center gap-2 rounded-md px-1 py-1.5 hover:bg-subtle">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-md font-medium text-foreground">{rule.name}</span>
                <span className="mt-1 flex flex-wrap gap-1">
                  {(rule.match.keywords ?? []).map((word) => (
                    <Pill key={word} tone="brand" size="sm">{word}</Pill>
                  ))}
                </span>
              </span>

              {rule.action.priority === 'urgent' ? (
                <Pill tone="danger" size="sm">Urgent</Pill>
              ) : null}

              <ToggleRow
                label="Enabled"
                checked={rule.enabled}
                onCheckedChange={(enabled) => toggle(rule, enabled)}
              />

              <IconButton
                icon={Trash2}
                label={`Delete rule ${rule.name ?? 'rule'}`}
                size="sm"
                onClick={() => remove(rule.id)}
              />
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={onSubmit} className="mt-3 flex flex-col gap-2.5">
        <Input
          label="Rule name"
          placeholder="Production incidents"
          size="sm"
          error={formState.errors.name?.message}
          {...register('name')}
        />
        <Input
          label="Keywords"
          hint="Comma separated, e.g. outage, p0, rollback"
          placeholder="outage, p0"
          size="sm"
          error={formState.errors.keywords?.message}
          {...register('keywords')}
        />
        <label className="flex cursor-pointer items-center gap-2.5 text-md text-secondary-foreground">
          <input type="checkbox" className="h-4 w-4 rounded-xs accent-primary" {...register('urgent')} />
          Treat matches as urgent (pierces quiet hours and Do not disturb)
        </label>
        <Button type="submit" variant="primary" size="sm" leftIcon={Plus} className="self-start">
          Add rule
        </Button>
      </form>
    </section>
  );
}
